import { ENotAuthNavigation } from '@hts/shared/enums';
import { IMappedSubmenuCategory } from '@hts/shared/types';
import { HtsLink, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { removeComponentProps, throttle } from '@hts/shared/utils';
import React, { HTMLProps, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/Header/MenusDropdown.module.scss';

interface IAllSimchasDropdownProps {
  label: string;
  categories: IMappedSubmenuCategory[];
  fullWidth?: boolean;
  containerProps?: HTMLProps<HTMLDivElement>;
  menuContainerProps?: HTMLProps<HTMLDivElement>;
  disabled?: boolean;
}

export const AllSimchasDropdown: React.FC<IAllSimchasDropdownProps> = memo(
  ({ label, categories, fullWidth, containerProps, menuContainerProps, disabled = false }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLUListElement>(null);

    const [isOpen, setOpen] = useState(false);
    const [dropdownOffsetLeft, setDropdownOffsetLeft] = useState(0);
    const [availableWidth, setAvailableWidth] = useState(window.innerWidth);

    const newContainerProps = removeComponentProps(containerProps, ['className']);
    const newMenuContainerProps = removeComponentProps(menuContainerProps, ['className']);

    const containerClassName = [styles.container, containerProps?.className].join(' ');
    const menuContainerClassName = [styles.menuContainer, menuContainerProps?.className].join(' ');

    const containerStyles: React.CSSProperties = useMemo(
      () => ({ width: fullWidth ? '100%' : 'max-content', opacity: disabled ? 0.7 : 1 }),
      [fullWidth, disabled]
    );

    const menuStyles: React.CSSProperties = useMemo(() => {
      const result: React.CSSProperties = { ...menuContainerProps?.style };

      const maxWidth = availableWidth - dropdownOffsetLeft + 38;

      result.visibility = isOpen ? 'visible' : 'hidden';
      result.opacity = isOpen ? 1 : 0;
      result.width = maxWidth;

      return result;
    }, [menuContainerProps?.style, availableWidth, dropdownOffsetLeft, isOpen]);

    useEffect(() => {
      const updateDimensions = () => {
        const viewportWidth = window.visualViewport?.width;

        setAvailableWidth(viewportWidth ?? window.innerWidth);

        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();

          setDropdownOffsetLeft(rect.left);
        }
      };

      const throttledHandleResize = throttle(updateDimensions, 200);

      updateDimensions();

      window.addEventListener('resize', throttledHandleResize);

      return () => {
        window.removeEventListener('resize', throttledHandleResize);
      };
    }, []);

    const onMouseEnter = useCallback(() => {
      setOpen(true);
    }, []);

    const onMouseLeave = useCallback(() => {
      setOpen(false);
    }, []);

    const onTouchStart = useCallback(() => {
      setOpen(true);
    }, []);

    const renderMenuItems = useCallback(
      (
        { categories, id, fileUrl: mediaPath, name, slug }: IMappedSubmenuCategory,
        index: number,
        arr: IMappedSubmenuCategory[]
      ) => (
        <li key={`${id}-${index}`} className={styles.categoryItemContainer}>
          <HtsLink
            label={name}
            redirectTo={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${slug}`}
            typographyProps={{ fontWeight: '500' }}
            linkProps={{ onClick: () => setOpen(false) }}
          />

          {Boolean(categories.length) && (
            <ul>
              {categories.map((subCategory, i) => (
                <li key={`${subCategory.id}-${i}`}>
                  <HtsLink
                    label={subCategory.name}
                    redirectTo={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${slug}?sub=${subCategory.slug}`}
                    typographyProps={{ variant: 'body2', fontWeight: '300' }}
                    linkProps={{ onClick: () => setOpen(false) }}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      ),
      []
    );

    return (
      <div
        className={containerClassName}
        ref={dropdownRef}
        style={containerStyles}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        {...newContainerProps}
      >
        <HtsTypography variant="body2" fontWeight="500" className={styles.label}>
          {label}
        </HtsTypography>

        <HtslIcon
          icon="keyboard-arrow-down-round"
          className={styles.icon}
          style={{ transform: `rotate(${isOpen ? '-180deg' : '0deg'})` }}
        />

        {Boolean(categories.length) && (
          <ul
            ref={contentRef}
            className={menuContainerClassName}
            style={menuStyles}
            {...newMenuContainerProps}
          >
            {categories.map(renderMenuItems)}
          </ul>
        )}
      </div>
    );
  }
);
